import React, { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAuth } from '../hooks/useAuth'
import { refreshTokenFailure } from '../store/authSlice'
import { isTokenExpired } from '../store/authSlice'
import { NoPermission } from '../components/common/PermissionWrapper'

/**
 * 高级路由守卫组件
 */
const RouteGuard = ({ 
  children, 
  requireAuth = true,
  adminOnly = false,
  permissions = null,
  requireAll = false,
  fallbackPath = '/login',
  checkTokenExpiry = true
}) => {
  const { 
    isAuthenticated, 
    user, 
    token, 
    hasPermission, 
    isAdmin, 
    hasAnyPermission, 
    hasAllPermissions,
    hasRoutePermission 
  } = useAuth()
  const dispatch = useDispatch()
  const location = useLocation()

  // 检查token是否过期
  useEffect(() => {
    if (checkTokenExpiry && isAuthenticated && token && isTokenExpired(token)) {
      console.log('Token expired, logging out...')
      dispatch(refreshTokenFailure())
    }
  }, [token, isAuthenticated, checkTokenExpiry, dispatch])

  // 如果需要认证但用户未登录
  if (requireAuth && !isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  // 如果不需要认证但用户已登录（如登录页）
  if (!requireAuth && isAuthenticated) {
    const from = location.state?.from?.pathname || '/dashboard'
    return <Navigate to={from} replace />
  }

  // 如果需要认证且用户已登录，进行权限检查
  if (requireAuth && isAuthenticated) {
    // 检查管理员权限
    if (adminOnly && !isAdmin()) {
      return (
        <div className="route-guard-error">
          <NoPermission message="此页面仅限管理员访问" />
        </div>
      )
    }

    // 检查特定权限
    if (permissions && Array.isArray(permissions)) {
      const hasAccess = requireAll 
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions)
      
      if (!hasAccess) {
        return (
          <div className="route-guard-error">
            <NoPermission message="您没有权限访问此页面" />
          </div>
        )
      }
    }

    // 检查路由权限
    if (!hasRoutePermission(location.pathname)) {
      return (
        <div className="route-guard-error">
          <NoPermission message="您没有权限访问此页面" />
        </div>
      )
    }
  }

  return children
}

/**
 * 受保护的路由组件
 */
export const ProtectedRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} {...props}>
    {children}
  </RouteGuard>
)

/**
 * 公开路由组件（已登录用户会被重定向）
 */
export const PublicRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={false} checkTokenExpiry={false} {...props}>
    {children}
  </RouteGuard>
)

/**
 * 管理员专用路由组件
 */
export const AdminRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} adminOnly={true} {...props}>
    {children}
  </RouteGuard>
)

/**
 * 基于权限的路由组件
 */
export const PermissionRoute = ({ children, permissions, requireAll = false, ...props }) => (
  <RouteGuard 
    requireAuth={true} 
    permissions={permissions} 
    requireAll={requireAll} 
    {...props}
  >
    {children}
  </RouteGuard>
)

export default RouteGuard 